import React, { useState } from 'react';
import Icon from '../AppIcon';

const FormInput = ({
  label,
  type = 'text',
  value,
  onChange,
  onBlur,
  placeholder,
  required = false,
  error,
  success = false,
  disabled = false,
  description,
  icon,
  prefix,
  suffix,
  maxLength,
  helpText,
  // Explicit aria overrides
  ariaLabel,
  name,
  id: idProp,
  ...props
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const inputId = idProp || `input-${(label || name || 'field')?.replace(/[^a-zA-Z0-9]/g, '-')?.toLowerCase()}`;
  const errorId = `${inputId}-error`;
  const descId = `${inputId}-description`;
  const hintId = `${inputId}-hint`;

  const describedByIds = [
    error ? errorId : null,
    description ? descId : null,
    helpText ? hintId : null
  ]?.filter(Boolean)?.join(' ');

  const getInputClasses = () => {
    let classes = 'flex w-full rounded-md border bg-background py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200';
    
    const hasLeftAddon = icon || prefix;
    const hasRightAddon = success || error || suffix;
    
    if (hasLeftAddon && hasRightAddon) {
      classes += ' pl-10 pr-10';
    } else if (hasLeftAddon) {
      classes += ' pl-10 pr-3';
    } else if (hasRightAddon) {
      classes += ' pl-3 pr-10';
    } else {
      classes += ' px-3';
    }
    
    if (error) {
      classes += ' border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500';
    } else if (success) {
      classes += ' border-green-500 focus-visible:ring-green-500 focus-visible:border-green-500';
    } else {
      classes += ' border-input focus-visible:ring-ring';
    }
    
    return classes;
  };

  const charCount = typeof value === 'string' ? value?.length : 0;

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="flex items-center gap-1.5">
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-foreground flex items-center gap-1"
          >
            {label}
            {required && (
              <span className="text-red-500" aria-hidden="true">*</span>
            )}
          </label>
          {helpText && (
            <div className="relative inline-flex">
              <button
                type="button"
                className="flex items-center justify-center w-4 h-4 rounded-full bg-muted hover:bg-muted-foreground/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onFocus={() => setShowTooltip(true)}
                onBlur={() => setShowTooltip(false)}
                aria-label={`Help for ${label}: ${helpText}`}
                aria-describedby={hintId}
                tabIndex={0}
              >
                <Icon name="Info" size={10} color="var(--color-muted-foreground)" strokeWidth={2.5} aria-hidden="true" />
              </button>
              {showTooltip && (
                <div
                  id={hintId}
                  role="tooltip"
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-52 px-3 py-2 bg-foreground text-background text-xs rounded-lg shadow-lg pointer-events-none"
                >
                  {helpText}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-foreground" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="relative">
        {(icon || prefix) && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center" aria-hidden="true">
            {icon ? (
              <Icon name={icon} size={16} color="var(--color-muted-foreground)" strokeWidth={2} />
            ) : (
              <span className="text-sm font-medium text-muted-foreground">{prefix}</span>
            )}
          </div>
        )}
        <input
          id={inputId}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          className={getInputClasses()}
          aria-label={ariaLabel || label}
          aria-required={required ? 'true' : undefined}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={describedByIds || undefined}
          aria-disabled={disabled ? 'true' : undefined}
          {...props}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1" aria-hidden="true">
          {suffix && !success && !error && (
            <span className="text-sm font-medium text-muted-foreground">{suffix}</span>
          )}
          {success && !error && (
            <Icon name="CheckCircle" size={16} color="#16a34a" strokeWidth={2} />
          )}
          {error && (
            <Icon name="XCircle" size={16} color="#dc2626" strokeWidth={2} />
          )}
        </div>
      </div>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          {error && (
            <p
              id={errorId}
              className="text-xs text-red-500 flex items-center gap-1"
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
            >
              <Icon name="AlertCircle" size={12} color="#dc2626" strokeWidth={2} aria-hidden="true" />
              {error}
            </p>
          )}
          {!error && description && (
            <p id={descId} className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        {maxLength && (
          <span
            className={`text-xs flex-shrink-0 ${
              charCount >= maxLength ? 'text-red-500 font-medium' : 
              charCount >= maxLength * 0.8 ? 'text-warning' : 'text-muted-foreground'
            }`}
            aria-label={`${charCount} of ${maxLength} characters used`}
          >
            {charCount}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
};

export default FormInput;